import {useState, useEffect, useMemo} from 'react';
import {useDocsetManifest} from '../../context/DocsetContext';
import type {DocNode} from '../../types/docset';
import type {ContentState} from '../../types/content';
import {NavSidebar} from '../navigation/NavSidebar';
import {ContentViewport} from './ContentViewport';
import {HeaderBar} from './HeaderBar';
import {resolveResource} from '../../utils/resources';
import {DocsetStyles} from './DocsetStyles';
import clsx from 'clsx';

type DocsetLayoutProps = {
  activeNode?: DocNode;
  contentState: ContentState;
};

export function DocsetLayout({activeNode, contentState}: DocsetLayoutProps) {
  const manifest = useDocsetManifest();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const accentColor = manifest.theme?.accentColor ?? '#1e88e5';
  const activeTitle = useMemo(() => activeNode?.title ?? manifest.title, [activeNode?.title, manifest.title]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [manifest.id, activeNode?.pathSegments.join('/')]);

  return (
    <div className="flex h-screen overflow-hidden bg-slate-950" style={{color: 'inherit', ['--accent-color' as any]: accentColor}}>
      <DocsetStyles />
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 w-72 flex-shrink-0 transform border-r border-slate-800/60 bg-slate-950/95 backdrop-blur transition-transform lg:static lg:w-80 lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div className="flex h-full flex-col">
          <div className="flex h-20 items-center gap-3 border-b border-slate-800/60 px-6">
            {manifest.assets.logo ? (
              <img
                src={resolveResource(manifest, manifest.assets.logo)}
                alt={`${manifest.title} logo`}
                className="h-12 rounded-lg bg-slate-900/80 object-contain"
              />
            ) : (
              <div
                className="flex h-12 items-center justify-center rounded-xl text-lg font-semibold"
                style={{backgroundColor: `${accentColor}33`, color: accentColor}}
              >
                {manifest.title.slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <div className="border-b border-slate-800/60 px-6 py-4">
            <h2 className="text-base font-semibold tracking-tight text-white">{manifest.title}</h2>
          </div>
          <NavSidebar activeNode={activeNode} onNavigate={() => setSidebarOpen(false)} />
        </div>
      </aside>

      {sidebarOpen ? (
        <div className="fixed inset-0 z-30 bg-slate-950/70 backdrop-blur-sm lg:hidden" onClick={() => setSidebarOpen(false)} />
      ) : null}

      <div className="flex h-full flex-1 flex-col overflow-hidden bg-white">
        <HeaderBar
          accentColor={accentColor}
          isSidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((open) => !open)}
          onNavigate={() => setSidebarOpen(false)}
          activeTitle={activeTitle}
        />
        <ContentViewport state={contentState} activeNode={activeNode} />
      </div>
    </div>
  );
}
